import { useMemo, useId } from 'react';
import { FormattedMessage } from 'react-intl';
import LoadingDots from '@alltrails/denali/components/LoadingDots';
import TabBar from '@alltrails/denali/components/TabBar';
import classNames from 'classnames';
import { OnSelect, RenderResultContent, ResultBase, Results, SearchResultsEmptyState, SearchResultsTabBar } from '../../../types/searchBoxTypes';
import { SearchIds } from '../../../hooks/useSearchIds';
import NlSearchModule from '../../AlgoliaSearchBox/NlSearchModule';
import SectionHeader from './SectionHeader';
import ResultItem from './ResultItem';
import styles from './styles/CustomSearchResults.module.scss';

const EmptyState = ({ primary, secondary }: SearchResultsEmptyState) => {
  return (
    <div className={styles.emptyState}>
      <div className={styles.primary}>{primary}</div>
      <div className={styles.secondary}>{secondary}</div>
    </div>
  );
};

type CustomSearchResultsProps<T extends ResultBase> = {
  activeId?: string;
  areResultsLoading?: boolean;
  className?: string;
  emptyState?: SearchResultsEmptyState;
  onSelect: OnSelect<T>;
  preventScrollOnResultSelection?: boolean;
  query: string;
  results: Results<T>;
  renderResultContent: RenderResultContent<T>;
  searchResultsTabBar?: SearchResultsTabBar;
  handleOnAiSearch?: (text: string) => void;
  searchIds?: SearchIds;
};

const CustomSearchResults = <T extends ResultBase>({
  activeId,
  areResultsLoading,
  className,
  emptyState,
  onSelect,
  preventScrollOnResultSelection,
  query,
  results,
  renderResultContent,
  searchResultsTabBar,
  handleOnAiSearch,
  searchIds
}: CustomSearchResultsProps<T>) => {
  const { listboxId, searchLabelId, getOptionId } = searchIds || {};
  const searchCountId = useId();

  const tabBar = useMemo(() => {
    if (!searchResultsTabBar) {
      return null;
    }
    return <TabBar {...searchResultsTabBar} className={styles.tabBar} testId="search-tabs" />;
  }, [searchResultsTabBar]);

  const content = useMemo(() => {
    if (results.length === 0 && !query) {
      if (emptyState) {
        return <EmptyState {...emptyState} />;
      }
      return null;
    }
    if (results.length) {
      return (
        <>
          <div id={searchCountId} className="screenReadersOnly" aria-atomic="true">
            {results.length > 0 ? `${results.length} results found` : 'No results found'}
          </div>
          <ul
            role="listbox"
            {...(listboxId && { id: listboxId })}
            className={styles.resultsList}
            {...(searchLabelId && { 'aria-labelledby': searchLabelId })}
            {...(searchCountId && { 'aria-describedby': searchCountId })}
          >
            {results.map((result, index) => {
              if ('text' in result) {
                return <SectionHeader key={result.text} heading={result} />;
              }
              return (
                <li
                  key={result.id}
                  role="option"
                  aria-selected={activeId === result.id}
                  id={getOptionId ? getOptionId(result.id) : `${listboxId}-${result.id}`}
                >
                  <ResultItem
                    className={result.containerClassName}
                    isActive={activeId === result.id}
                    onClick={() => onSelect(result as T, { index, query })}
                    preventAutoScroll={preventScrollOnResultSelection}
                  >
                    {renderResultContent(result as T)}
                  </ResultItem>
                </li>
              );
            })}
          </ul>
        </>
      );
    }
    if (areResultsLoading) {
      return (
        <div className={styles.loadingState}>
          <LoadingDots testId="search-loading" />
        </div>
      );
    }
    return (
      <EmptyState
        primary={<FormattedMessage defaultMessage="No results" />}
        secondary={<FormattedMessage defaultMessage="We couldn't find anything matching {query}" values={{ query }} />}
      />
    );
  }, [
    activeId,
    areResultsLoading,
    emptyState,
    onSelect,
    preventScrollOnResultSelection,
    query,
    renderResultContent,
    results,
    getOptionId,
    listboxId,
    searchCountId,
    searchLabelId
  ]);

  const NlSearch = useMemo(() => {
    if (!handleOnAiSearch) {
      return null;
    }

    return <NlSearchModule query={query} handleOnAiSearch={handleOnAiSearch} isActive={!Boolean(activeId)} />;
  }, [handleOnAiSearch, query, activeId]);

  return tabBar || content || NlSearch ? (
    // prevent blurring the input when clicking inside the dropdown
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={classNames(styles.container, className)} onMouseDown={e => e.preventDefault()}>
      <div className={styles.scrollable}>
        {NlSearch}
        {tabBar}
        {content}
      </div>
    </div>
  ) : null;
};

export default CustomSearchResults;
